import { Icon } from '@ketch-com/deck'
import { Box, Typography } from '@mui/material'
import { FormAssigneeOrTeamSelector } from 'components/form/FormAssigneeOrTeamSelector'
import { FormInput } from 'components/form/FormInput'
import { RadioGroup } from 'components/RadioGroup/RadioGroup'
import { useFormikContext } from 'formik'
import { AssessmentCreateFormValues } from '../formHelpers'
import { AssessmentAttributeInitialAssigneeType } from '@ketch-com/windlass/dist/assessments/externalUser'
import { useCallback } from 'react'
import { InfoIconWithTooltip } from './InfoIconWithTooltip'

export interface AssigneeSelectorBlockProps {
  assignationType: AssessmentAttributeInitialAssigneeType
  setAssignationType: (type: AssessmentAttributeInitialAssigneeType) => void
  setSelectedExternalUser?: (externalUser: string | undefined) => void
}

export const AssigneeSelectorBlock: React.FC<AssigneeSelectorBlockProps> = ({
  assignationType,
  setAssignationType,
  setSelectedExternalUser,
}) => {
  const { values: formValues } = useFormikContext<AssessmentCreateFormValues>()
  const handleRadioChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAssignationType(e.target.value as AssessmentAttributeInitialAssigneeType)
      formValues.assignee = ''
      if (setSelectedExternalUser) {
        setSelectedExternalUser(undefined)
      }
    },
    [formValues, setSelectedExternalUser, setAssignationType],
  )

  return (
    <Box mb={3}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="label"> Assign Question To</Typography>
        <InfoIconWithTooltip
          title={
            <>
              <Typography component="div">
                Choose the person or team to be initially assigned to all questions in this assessment. You can update
                this assignment once the assessment begins.
              </Typography>
              <Typography component="div" mt={1}>
                You can also send this assessment to an external party for collaboration. Simply select the "Send to
                Email" option and enter their address. Note: this person will not be added to your Ketch Team.
              </Typography>
            </>
          }
        />
      </Box>
      <RadioGroup
        name="assignationType"
        sx={{ gap: 4 }}
        options={[
          { title: 'Select User', value: AssessmentAttributeInitialAssigneeType.internal },
          { title: 'Send to Email (External)', value: AssessmentAttributeInitialAssigneeType.external },
          { title: `I'll Do It Later`, value: AssessmentAttributeInitialAssigneeType.later },
        ]}
        row
        value={assignationType}
        onChange={handleRadioChange}
      />
      {assignationType === AssessmentAttributeInitialAssigneeType.internal && (
        <FormAssigneeOrTeamSelector name="assignee" showAssignToMeButton={false} inFormRow={false} />
      )}
      {assignationType === AssessmentAttributeInitialAssigneeType.external && (
        <FormInput
          startIcon={<Icon name="OMail" />}
          formPropertyName="assignee"
          required
          placeholder="Enter Email Address"
          type="email"
          fullWidth
        />
      )}
    </Box>
  )
}
